import * as React from 'react'
import styled from 'styled-components'
import { agentService } from 'core/services'
import {
  IAgentTourSkill,
  IGuideTourSkillBulkAssignmentGuide,
  IGuideTourSkillBulkAssignmentPreview,
  IWindow,
} from 'static/interfaces'
import * as constants from 'static/constants'
import { Button, CheckBox, InputTextArea, Select, Spinner } from 'components/atoms'
import SvgIcon from 'components/atoms/SvgIcon'
import TourSkillSingleSelect, {
  ITourSkillOption,
} from 'components/molecules/TourSkill/TourSkillSingleSelect'

declare var window: IWindow

interface IProps {
  onSuccess?: (result: { agentTourSkillId: number; createdCount: number }) => void
}

const guideName = (guide: IGuideTourSkillBulkAssignmentGuide): string =>
  guide.fullname || guide.username || '-'

const assignedLevelLabel = (guide: IGuideTourSkillBulkAssignmentGuide): string | undefined =>
  constants.SKILL_LEVEL_OPTIONS.find(option => Number(option.value) === guide.assigned_level)?.label

const AssignmentStatus: React.FC<{ guide: IGuideTourSkillBulkAssignmentGuide }> = ({ guide }) => {
  const levelLabel = assignedLevelLabel(guide)
  return (
    <Status className={guide.already_assigned ? 'existing' : 'new'}>
      {guide.already_assigned
        ? `保有済み${levelLabel ? `（${levelLabel}）` : ''}・変更なし`
        : '新規付与'}
    </Status>
  )
}

const GuideTourSkillBulkAssignmentModal: React.FC<IProps> = ({ onSuccess }) => {
  const [step, setStep] = React.useState<1 | 2>(1)
  const [tourSkills, setTourSkills] = React.useState<IAgentTourSkill[]>([])
  const [tourSkillId, setTourSkillId] = React.useState('')
  const [identifiers, setIdentifiers] = React.useState('')
  const [preview, setPreview] = React.useState<IGuideTourSkillBulkAssignmentPreview | null>(null)
  const [selectedGuideIds, setSelectedGuideIds] = React.useState<number[]>([])
  const [level, setLevel] = React.useState('3')
  const [isLoading, setIsLoading] = React.useState(false)

  const selectedTourSkill = React.useMemo(
    () => tourSkills.find(tourSkill => String(tourSkill.id) === tourSkillId),
    [tourSkills, tourSkillId]
  )

  const tourSkillOptions = React.useMemo<ITourSkillOption[]>(
    () =>
      tourSkills.map(tourSkill => ({
        value: String(tourSkill.id),
        label: tourSkill.name,
      })),
    [tourSkills]
  )

  React.useEffect(() => {
    agentService
      .getAgentTourSkills()
      .then(({ agent_tour_skills }) =>
        setTourSkills([...agent_tour_skills].sort((a, b) => (b.position || 0) - (a.position || 0)))
      )
      .catch(e => console.error(e))
  }, [])

  React.useEffect(() => {
    window.globalModal.updateModalConfig({ width: step === 1 ? '640px' : '860px' })
  }, [step])

  const handlePreview = React.useCallback(async () => {
    if (!tourSkillId || identifiers.trim() === '') {
      return
    }

    setIsLoading(true)
    try {
      const result = await agentService.previewGuideTourSkillBulkAssignment({
        agent_tour_skill_id: Number(tourSkillId),
        identifiers,
      })
      setPreview(result)
      setSelectedGuideIds([])
      setStep(2)
    } catch (e) {
      // 422/403 等は httpClient のインターセプタが flash / モーダルで表示する
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [tourSkillId, identifiers])

  const toggleGuide = React.useCallback((guideId: number) => {
    setSelectedGuideIds(current =>
      current.includes(guideId)
        ? current.filter(selectedGuideId => selectedGuideId !== guideId)
        : current.concat(guideId)
    )
  }, [])

  const directlyMatchedGuideIds = React.useMemo(
    () =>
      (preview?.matched || [])
        .filter(match => !match.guide.already_assigned)
        .map(match => match.guide.id),
    [preview]
  )

  const guideIdsToAssign = React.useMemo(() => {
    return Array.from(new Set(directlyMatchedGuideIds.concat(selectedGuideIds)))
  }, [directlyMatchedGuideIds, selectedGuideIds])

  const unselectedAmbiguousCount = React.useMemo(
    () =>
      (preview?.ambiguous || []).filter(
        result =>
          !result.candidates.some(
            guide =>
              guide.already_assigned ||
              directlyMatchedGuideIds.includes(guide.id) ||
              selectedGuideIds.includes(guide.id)
          )
      ).length,
    [preview, directlyMatchedGuideIds, selectedGuideIds]
  )

  const visibleSummaryCount = React.useMemo(() => {
    if (!preview) {
      return 0
    }

    return [preview.summary.matched, preview.summary.ambiguous, preview.summary.not_found].filter(
      count => count > 0
    ).length
  }, [preview])

  const handleAssign = React.useCallback(async () => {
    if (!tourSkillId || guideIdsToAssign.length === 0) {
      return
    }

    setIsLoading(true)
    try {
      const result = await agentService.createGuideTourSkillBulkAssignment({
        agent_tour_skill_id: Number(tourSkillId),
        guide_ids: guideIdsToAssign,
        level: Number(level),
      })
      window.flashMessages.addMessage({
        text: result.flush.message,
        type: result.flush.type,
      })
      window.globalModal.closeModal()
      if (onSuccess) {
        onSuccess({
          agentTourSkillId: Number(tourSkillId),
          createdCount: result.created_count,
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }, [tourSkillId, guideIdsToAssign, level, onSuccess])

  const stepOneNote = !tourSkillId
    ? 'ツアースキルを選ぶと照合できます'
    : identifiers.trim() === ''
    ? 'ガイドを入力してください'
    : ''

  return (
    <Wrapper>
      <header>
        <h2>
          {step === 1 ? 'ツアースキル一括付与' : `照合結果：${selectedTourSkill?.name || ''}`}
        </h2>
        <SvgIcon
          className="close"
          name="close"
          width={24}
          height={24}
          role="button"
          onClick={() => window.globalModal.closeModal()}
        />
      </header>

      <main>
        {step === 1 ? (
          <StepOne>
            <Field className="tourSkill">
              <label>ツアースキル</label>
              <TourSkillSingleSelect
                className="TourSkillSelect"
                options={tourSkillOptions}
                value={tourSkillId || null}
                onChange={value => setTourSkillId(value || '')}
                placeholder="選択してください"
                isClearable={true}
              />
            </Field>
            <Field className="identifiers">
              <label>付与するガイド（1行に1人）</label>
              <p>
                メールアドレス・ID・名前（苗字のみも可）・ユーザー名のどれかで指定できます
                <br />
                改行・カンマ・タブ区切りに対応。スプレッドシートの列をそのまま貼り付けられます。
                <br />
                一度に500件まで照合できます。
              </p>
              <InputTextArea
                name="bulk_assignment_identifiers"
                value={identifiers}
                maxLength={50_000}
                resizable={true}
                onChangeHandler={event => setIdentifiers(event.target.value)}
              />
            </Field>
          </StepOne>
        ) : (
          preview && (
            <StepTwo>
              {visibleSummaryCount > 1 && (
                <Summary>
                  {preview.summary.matched > 0 && (
                    <span className="matched">一致 {preview.summary.matched}</span>
                  )}
                  {preview.summary.ambiguous > 0 && (
                    <span className="ambiguous">候補が複数 {preview.summary.ambiguous}</span>
                  )}
                  {preview.summary.not_found > 0 && (
                    <span className="notFound">見つからない {preview.summary.not_found}</span>
                  )}
                </Summary>
              )}

              {preview.matched.length > 0 && (
                <ResultSection>
                  <SectionTitle>
                    一致 <CountPill className="matched">{preview.matched.length}</CountPill>
                  </SectionTitle>
                  <TableScroll>
                    <MatchTable>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>本名</th>
                          <th>メールアドレス</th>
                          <th>状態</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.matched.map(match => (
                          <tr key={match.guide.id}>
                            <td className="id">{match.guide.id}</td>
                            <td>{guideName(match.guide)}</td>
                            <td>{match.guide.email}</td>
                            <td>
                              <AssignmentStatus guide={match.guide} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </MatchTable>
                  </TableScroll>
                </ResultSection>
              )}

              {preview.ambiguous.length > 0 && (
                <ResultSection>
                  <SectionTitle>
                    候補が複数{' '}
                    <CountPill className="ambiguous">{preview.ambiguous.length}</CountPill>
                  </SectionTitle>
                  {preview.ambiguous.map(result => (
                    <CandidateGroup key={`${result.type}-${result.input}`}>
                      <h4>
                        「{result.input}」に {result.candidates.length}
                        件ヒット。付与する人を選んでください
                      </h4>
                      {result.candidates.map(guide => {
                        const isDirectlyMatched = directlyMatchedGuideIds.includes(guide.id)
                        const isFixed = guide.already_assigned || isDirectlyMatched
                        return (
                          <GuideRow key={guide.id}>
                            <CheckBox
                              name={`bulk_assignment_guide_${result.input}_${guide.id}`}
                              checked={isFixed || selectedGuideIds.includes(guide.id)}
                              disabled={isFixed}
                              onChangeHandler={() => toggleGuide(guide.id)}
                            />
                            <CandidateDetails>
                              <span>{guideName(guide)}</span>
                              <span className="email">{guide.email}</span>
                            </CandidateDetails>
                            <AssignmentStatus guide={guide} />
                          </GuideRow>
                        )
                      })}
                    </CandidateGroup>
                  ))}
                  <Hint>チェックしていない候補には付与されません。</Hint>
                </ResultSection>
              )}

              {preview.not_found.length > 0 && (
                <ResultSection>
                  <SectionTitle>
                    見つからない{' '}
                    <CountPill className="notFound">{preview.not_found.length}</CountPill>
                  </SectionTitle>
                  <NotFoundList>
                    {preview.not_found.map(result => (
                      <li key={`${result.type}-${result.input}`}>{result.input}</li>
                    ))}
                  </NotFoundList>
                  <Hint>付与されません。直す場合は「戻る」で入力し直してください。</Hint>
                </ResultSection>
              )}
            </StepTwo>
          )
        )}
      </main>

      <footer>
        {step === 1 ? (
          <>
            <FooterNote>{stepOneNote}</FooterNote>
            <Button handleClick={() => window.globalModal.closeModal()} disabled={isLoading}>
              キャンセル
            </Button>
            <Button
              primary={true}
              handleClick={handlePreview}
              disabled={isLoading || !tourSkillId || identifiers.trim() === ''}
            >
              {isLoading ? <Spinner /> : '照合する'}
            </Button>
          </>
        ) : (
          <>
            {guideIdsToAssign.length > 0 && unselectedAmbiguousCount === 0 && (
              <LevelField>
                <strong>スキルランク</strong>
                <Select
                  name="bulk_assignment_level"
                  value={level}
                  options={constants.SKILL_LEVEL_OPTIONS}
                  onChangeHandler={event => setLevel(event.target.value)}
                />
                <p>全員に同じランクで付与します。保有済みの人のランクは変更しません。</p>
              </LevelField>
            )}
            <FooterNote>
              {unselectedAmbiguousCount > 0
                ? `候補が未選択の行が ${unselectedAmbiguousCount}件 あります`
                : ''}
            </FooterNote>
            <Button handleClick={() => setStep(1)} disabled={isLoading}>
              戻る
            </Button>
            <Button
              primary={true}
              handleClick={handleAssign}
              disabled={isLoading || guideIdsToAssign.length === 0 || unselectedAmbiguousCount > 0}
            >
              {isLoading ? <Spinner /> : `${guideIdsToAssign.length}人に付与する`}
            </Button>
          </>
        )}
      </footer>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  color: ${constants.COLORS.black};
  font-size: 14px;
  line-height: 1.6;

  header,
  footer {
    display: flex;
    align-items: center;
  }

  header {
    padding: 18px 24px;
    justify-content: space-between;
    border-bottom: 1px solid ${constants.COLORS.divider};
    h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 500;
    }
    .close {
      color: ${constants.COLORS.subText};
      cursor: pointer;
    }
  }

  main {
    max-height: calc(100dvh - 220px);
    padding: 22px 24px;
    overflow-y: auto;
  }

  footer {
    min-height: 72px;
    padding: 16px 24px;
    justify-content: flex-end;
    gap: 14px;
    border-top: 1px solid ${constants.COLORS.divider};

    .Button + .Button {
      margin-left: 0;
    }
  }
`

const StepOne = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const StepTwo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const Field = styled.div`
  > label {
    display: block;
    margin-bottom: 4px;
    color: ${constants.COLORS.subText};
    font-size: 14px;
    font-weight: bold;
  }

  &.tourSkill {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 20px;

    > label {
      margin: 0;
      color: ${constants.COLORS.black};
      font-size: 15px;
      font-weight: normal;
    }

    > div {
      width: min(100%, 260px);
    }
  }

  &.identifiers {
    .InputTextArea textarea {
      min-height: 220px;
      height: 220px;
      box-sizing: border-box;
      padding: 14px 16px;
      line-height: 1.8;
    }
  }

  p {
    margin: 0 0 8px;
    color: ${constants.COLORS.subText};
    font-size: 11px;
    line-height: 16px;
  }
`

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  span {
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: bold;
    white-space: nowrap;
  }
  .matched {
    color: ${constants.COLORS.primary};
    background: ${constants.COLORS.successBG2};
  }
  .ambiguous {
    color: ${constants.COLORS.useallyText};
    background: ${constants.COLORS.useallyBG};
  }
  .notFound {
    color: ${constants.COLORS.cautionText};
    background: ${constants.COLORS.cautionBG};
  }
`

const ResultSection = styled.section`
  min-width: 0;
`

const SectionTitle = styled.h3`
  margin: 0 0 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`

const CountPill = styled.span`
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: bold;

  &.matched {
    color: ${constants.COLORS.primary};
    background: ${constants.COLORS.successBG2};
  }
  &.ambiguous {
    color: ${constants.COLORS.useallyText};
    background: ${constants.COLORS.useallyBG};
  }
  &.notFound {
    color: ${constants.COLORS.cautionText};
    background: ${constants.COLORS.cautionBG};
  }
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const MatchTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 8px 10px;
    border-bottom: 1px solid ${constants.COLORS.divider};
    color: ${constants.COLORS.subText};
    font-size: 12px;
    font-weight: bold;
    text-align: left;
    white-space: nowrap;
  }

  td {
    padding: 9px 10px;
    border-bottom: 1px solid ${constants.COLORS.divider2};
    font-size: 13px;
    overflow-wrap: anywhere;

    &.id {
      color: ${constants.COLORS.textLink};
      white-space: nowrap;
    }

    &:last-child {
      white-space: nowrap;
      overflow-wrap: normal;
    }
  }
`

const CandidateGroup = styled.div`
  overflow: hidden;
  margin-top: 8px;
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 5px;
  h4 {
    margin: 0;
    padding: 8px 12px;
    color: ${constants.COLORS.useallyText};
    background: ${constants.COLORS.useallyBG};
    font-size: 13px;
    font-weight: bold;
  }
`

const GuideRow = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  border-top: 1px solid ${constants.COLORS.divider2};
  font-size: 13px;

  .CheckBox {
    flex-shrink: 0;
  }
`

const CandidateDetails = styled.div`
  min-width: 0;
  flex: 1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  .email {
    color: ${constants.COLORS.subText};
    font-size: 12px;
    overflow-wrap: anywhere;
  }
`

const Status = styled.span`
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: bold;
  white-space: nowrap;
  &.new {
    color: ${constants.COLORS.primary};
    background: ${constants.COLORS.successBG2};
  }
  &.existing {
    color: ${constants.COLORS.subText};
    background: ${constants.COLORS.lightGray};
  }
`

const NotFoundList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  li {
    margin-top: 4px;
    padding: 7px 12px;
    color: ${constants.COLORS.cautionText};
    background: ${constants.COLORS.cautionBG};
    border-radius: 4px;
    font-size: 13px;
    overflow-wrap: anywhere;
  }
`

const Hint = styled.p`
  margin: 8px 0 0;
  color: ${constants.COLORS.subText};
  font-size: 12px;
`

const LevelField = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;

  > div {
    width: 80px;
  }

  p {
    margin: 0;
    color: ${constants.COLORS.subText};
    font-size: 12px;
  }
`

const FooterNote = styled.span`
  margin-right: auto;
  color: ${constants.COLORS.subText};
  font-size: 13px;
`

export default GuideTourSkillBulkAssignmentModal
